import React from 'react';
import { motion } from 'framer-motion';
import myImage from './images/Cyrus.jpg';
import CV from './images/Kibe_Cyrus_M_CV.pdf';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut' },
  }),
};

const stats = [
  { value: 'MUST', label: 'University' },
  { value: 'BSc.', label: 'IT Graduate' },
  { value: '2025', label: 'Graduate' },
];

const highlights = [
  { icon: '🌐', title: 'Web Development', desc: 'Building modern responsive full-stack web applications.' },
  { icon: '🎨', title: 'Graphic Design', desc: 'Creating brand identities, posters and marketing visuals.' },
];

const experience = [
  {
    role: 'IT Attachment',
    org: 'Ministry of Lands, Housing and Urban Planning',
    period: '2024',
    desc: 'Contributed to digital transformation initiatives, supported IT systems and analyzed data to enhance operational efficiency.',
    icon: '🏛️',
  },
  {
    role: 'IT Attachment',
    org: 'Mwai Kibaki Referral Hospital',
    period: '2025',
    desc: 'Supported hospital IT infrastructure, maintained systems and assisted in digitization of hospital records and processes.',
    icon: '🏥',
  },
];

const About = () => {
  return (
    <div className="relative py-32 px-6 bg-[#0a0f1e] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="absolute top-20 right-0 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-16"
        >
          <span className="font-mono text-cyan-400 text-sm">02.</span>
          <h2 className="text-3xl md:text-4xl font-black text-white">About Me</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative w-fit mx-auto lg:mx-0">
              <div className="absolute -inset-1 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl blur opacity-30" />
              <div className="relative rounded-2xl overflow-hidden border border-cyan-500/20 w-72 h-80 md:w-80 md:h-96">
  <img src={myImage} alt="Cyrus Maina Kibe" className="w-full h-full object-cover object-top" />
  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e]/60 to-transparent" />
</div>
             <div className="absolute -bottom-4 -right-4 bg-[#0d1427] border border-cyan-500/30 rounded-xl px-4 py-3 shadow-xl">
  <div className="text-cyan-400 font-black text-xl">1+</div>
  <div className="text-slate-400 text-xs">Years Exp.</div>
</div>
              <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-cyan-400 rounded-tl-lg" />
              <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-cyan-400 rounded-br-lg" />
            </div>

            <div className="flex gap-6 mt-10 justify-center lg:justify-start">
              {stats.map(({ value, label }) => (
                <div key={label} className="text-center">
                  <div className="text-cyan-400 font-black text-lg">{value}</div>
                  <div className="text-slate-500 text-xs mt-1">{label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              {highlights.map(({ icon, title, desc }) => (
                <div
                  key={title}
                  className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all duration-300 group"
                >
                  <div className="text-2xl mb-2">{icon}</div>
                  <div className="text-white font-semibold text-sm group-hover:text-cyan-400 transition-colors">{title}</div>
                  <div className="text-slate-500 text-xs mt-1 leading-relaxed">{desc}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="space-y-6">
            <motion.p
              variants={fadeUp}
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-slate-300 text-lg leading-relaxed"
            >
              Hi! I am{' '}
              <span className="text-cyan-400 font-semibold">Cyrus Maina Kibe</span>,
              a passionate Web Developer and Graphic Designer dedicated to building
              smart, efficient and visually engaging digital solutions.
            </motion.p>

            <motion.p
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-slate-400 leading-relaxed"
            >
              I recently graduated with a Bachelor of Science in Information Technology
              from Meru University of Science and Technology. My expertise spans
              front-end and back-end web development, UI design, and visual branding.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <span className="text-cyan-400">▹</span> Experience
              </h3>
              <div className="space-y-4">
                {experience.map(({ role, org, period, desc, icon }) => (
                  <div
                    key={org}
                    className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-lg flex-shrink-0">
                        {icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="text-white font-semibold text-sm group-hover:text-cyan-400 transition-colors">
                            {role}
                          </span>
                          <span className="text-cyan-400 font-mono text-xs">{period}</span>
                        </div>
                        <div className="text-slate-400 text-xs mt-0.5 mb-2">{org}</div>
                        <p className="text-slate-500 text-xs leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="pt-2"
            >
              
             <a   href={CV}
                download
                className="inline-flex items-center gap-3 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-[#0a0f1e] font-bold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/30 text-sm uppercase tracking-widest"
              >
                Download CV
              </a>
            </motion.div>
          </div>

        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
    </div>
  );
};

export default About;